'use client'

import { useQuery } from '@tanstack/react-query'
import { Lock } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Skeleton,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../context'

type Metric = RouterOutputs['achievement']['getBadgeProgress'][number]
type ConditionType = Metric['conditionType']

/** Minimal shape shared by both badge variants (getAvailableBadges / getAll's achievementBadge). */
interface LockedBadge {
  id: string
  title: string
  bonusRewardEnabled: boolean
  bonusXpValue: number | null
  bonusCoreSkillTag: { name: string } | null
}

interface LockedBadgeModalProps {
  className?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The locked badge to describe. */
  badge: LockedBadge
  /** Whose progress to show. Defaults to the signed-in user; pass when viewing another profile (e.g. a parent viewing a child). */
  userId?: string
}

/** Indonesian labels for each condition type — no per-condition label exists in the schema. */
const METRIC_LABEL: Record<ConditionType, string> = {
  TOTAL_BOOKS_COMPLETED: 'Buku selesai',
  TOTAL_READING_DURATION: 'Durasi baca',
  TOTAL_LESSONS_COMPLETED: 'Pelajaran selesai',
  TOTAL_XP_EARNED: 'Poin XP',
  CHALLENGE_COMPLETED: 'Tantangan selesai',
  READING_STAMINA: 'Stamina baca',
}

/** Duration-based metrics store seconds; render them in minutes. */
const DURATION_TYPES = new Set<ConditionType>(['TOTAL_READING_DURATION', 'READING_STAMINA'])

function formatPair(type: ConditionType, current: number, target: number) {
  if (DURATION_TYPES.has(type)) {
    return `${Math.round(current / 60)}/${Math.round(target / 60)} mnt`
  }
  return `${Math.round(current)}/${Math.round(target)}`
}

function MetricBar({ metric }: { metric: Metric }) {
  const { conditionType, currentValue, targetValue, isCompleted } = metric
  const ratio = targetValue > 0 ? Math.min(1, Math.max(0, currentValue / targetValue)) : isCompleted ? 1 : 0

  return (
    <div className='srd:flex srd:flex-col srd:gap-1'>
      <span className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-neutral-black')}>
        {METRIC_LABEL[conditionType]}
      </span>
      <div className='srd:flex srd:items-center srd:gap-2'>
        <div className='srd:h-2.5 srd:flex-1 srd:overflow-hidden srd:rounded-full srd:bg-grey-20'>
          <div className='srd:h-full srd:rounded-full srd:bg-purple-100' style={{ width: `${ratio * 100}%` }} />
        </div>
        <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:shrink-0 srd:text-grey-100')}>
          {formatPair(conditionType, currentValue, targetValue)}
        </span>
      </div>
    </div>
  )
}

/**
 * Detail modal for a *locked* (not-yet-earned) badge. Shows each condition as a
 * progress bar toward its target, plus the bonus reward — mirroring the earned
 * `BadgeDetailsModal` but for badges the user is still working toward.
 */
export function LockedBadgeModal({ className, open, onOpenChange, badge, userId }: LockedBadgeModalProps) {
  const trpc = useSharedTRPC()

  const {
    data: metrics,
    isPending,
    isError,
  } = useQuery({
    ...trpc.achievement.getBadgeProgress.queryOptions({ badgeId: badge.id, userId }),
    enabled: open,
  })

  const showReward = badge.bonusRewardEnabled && badge.bonusXpValue != null && badge.bonusXpValue > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('srd:max-w-sm', className)} showCloseButton={false}>
        <DialogTitle className='srd:sr-only'>Detail Lencana Terkunci</DialogTitle>
        <DialogDescription className='srd:sr-only'>Progres menuju lencana yang belum diperoleh</DialogDescription>

        <div className='srd:flex srd:flex-col srd:items-center srd:gap-5 srd:pt-2'>
          {/* Locked badge icon */}
          <div className='srd:flex srd:size-40 srd:items-center srd:justify-center srd:rounded-full srd:bg-purple-30'>
            <Lock className='srd:size-16 srd:text-neutral-white' />
          </div>

          {/* Badge name */}
          <h3
            className={cn(
              typographyVariants({ variant: 'headline-sm' }),
              'srd:line-clamp-2 srd:text-center srd:text-neutral-black',
            )}
          >
            {badge.title}
          </h3>

          {/* Metric progress bars */}
          <div className='srd:flex srd:w-full srd:flex-col srd:gap-3'>
            {isPending ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className='srd:h-9 srd:w-full srd:rounded-md' />
              ))
            ) : isError ? (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-center srd:text-grey-100')}>
                Gagal memuat progres.
              </p>
            ) : metrics && metrics.length > 0 ? (
              metrics.map((metric) => <MetricBar key={metric.conditionId} metric={metric} />)
            ) : (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-center srd:text-grey-100')}>
                Belum ada progres.
              </p>
            )}
          </div>

          {/* Bonus reward */}
          {showReward && (
            <p
              className={cn(
                typographyVariants({ variant: 'body-sm' }),
                'srd:text-center srd:font-bold srd:text-purple-100',
              )}
            >
              + Bonus {badge.bonusXpValue} poin{badge.bonusCoreSkillTag ? ` ${badge.bonusCoreSkillTag.name}` : ''}
            </p>
          )}

          <DialogClose
            render={
              <Button size='lg' variant='outline' className='srd:w-fit'>
                Kembali
              </Button>
            }
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
